import {
  Component,
  OnInit,
  ViewChild,
  TemplateRef,
  OnDestroy,
  Input,
} from '@angular/core';
import {
  LearningPathStudentDetail,
  LearningPathStudentActivityStatus,
  LearningPathStudentActivityGeneral,
} from '../../models/interfaces';
import { LearnpathService } from '../../services/learnpath.service';
import { Observable, Subscription } from 'rxjs';
import { tap, take, switchMap, filter, map } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  NgbModal,
  NgbModalRef,
  NgbModalOptions,
} from '@ng-bootstrap/ng-bootstrap';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LearningPathStudentActivityStatusLog } from '../../models/LearningPathStudentActivityStatusLog';
import { LabelService } from 'src/app/shared/framework/labels/services/label.service';

@Component({
  selector: 'app-learnpath-employee-detail',
  templateUrl: './learnpath-employee-detail.component.html',
  styleUrls: ['./learnpath-employee-detail.component.scss'],
})
export class LearnpathEmployeeDetailComponent implements OnInit, OnDestroy {
  public employee$: Observable<LearningPathStudentDetail>;
  public statusses: LearningPathStudentActivityStatus[] = [];
  public statusChangeForm: FormGroup;

  private _modalRef: NgbModalRef;
  private modalOptions: NgbModalOptions = { size: 'md' };
  private _subscriptions: Subscription[] = [];
  private _learningPathId = 0;

  @ViewChild('confirmStatusChange', { static: true })
  public confirmStatusChangeModal: TemplateRef<any>;

  @Input('learningPathId') learningPathId?: number;
  @Input('learningPathStudentId') learningPathStudentId?: number;

  public constructor(
    private _learningPathService: LearnpathService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _modalService: NgbModal,
    private _formBuilder: FormBuilder,
    public location: Location,
    public _toastrService: ToastrService,
    private labelService: LabelService
  ) { }

  public ngOnInit(): void {
    this.employee$ = this._route.params.pipe(
      switchMap((params: Params) => {
        return this._learningPathService.getStudentDetail(
          +params.studentId || this.learningPathStudentId, true
        );
      }),
      map((detail: LearningPathStudentDetail) => {
        this._learningPathId = detail.learningPath.learningPathId;

        detail.studentActivities = detail.studentActivities.map(
          (activity: LearningPathStudentActivityGeneral) => {
            if (activity.activityTypeId === 6) {
              activity.file$ = this._learningPathService.getActivityAttachments(
                activity.studentActivityId
              );
            }
            return activity;
          }
        );
        return detail;
      })
    );

    this._subscriptions.push(
      this._learningPathService.getStudentActivityStatusses().subscribe((e) => {
        this.statusses = e;
      })
    );
  }

  public unsubscribe(studentId: number): void {
    this._subscriptions.push(
      this._learningPathService.unsubscribeEmployee(studentId).subscribe(() => {
        this._learningPathService.getListData(true);
        this._modalService.dismissAll();

        let route = this._router.url;

        if (route != "/lms/learnpaths/manager") {
          this._router.navigate([
            '/lms/learnpaths/external',
            this._learningPathId,
            'employees',
            'individual',
          ]);
        }
      })
    );
  }

  public requestChangeStatus(
    event: Event,
    studentId: number,
    activityId: number,
    statusId: number
  ): void {
    (event.target as HTMLInputElement).blur();
    this.statusChangeForm = this._formBuilder.group({
      message: '',
      activityId: activityId,
      studentId: studentId,
      statusId: statusId,
    });
    this._modalRef = this._modalService.open(
      this.confirmStatusChangeModal,
      this.modalOptions
    );
  }

  public changeStatus(): void {
    this._subscriptions.push(
      this._learningPathService
        .changeStudentActivityStatus(
          this.statusChangeForm.value.activityId,
          this.statusChangeForm.value.statusId,
          this.statusChangeForm.value.message
        )
        .pipe(
          take(1),
          tap(() => {
            this._learningPathService.getStudentDetail(
              this.statusChangeForm.value.studentId,
              true
            );
            this._modalRef.dismiss();
          })
        )
        .subscribe()
    );
  }

  public cancelChangeStatus(): void {
    this._modalRef.dismiss();
    this._learningPathService.getStudentDetail(
      this.statusChangeForm.value.studentId,
      true
    );
  }

  public getActivityStatus(activityStatusId: number): string {
    return this.statusses.filter(
      (e) => e.learningPathStudentActivityStatusId == activityStatusId
    )[0].label;
  }

  public getActivityClass(
    activity: LearningPathStudentActivityGeneral
  ): string {
    switch (activity.activityStatusId) {
      case 1:
        return 'notStarted';
      case 2:
        return 'started';
      case 3:
        return 'completed';
    }
  }

  public saveManagerComment(
    studentActivity: LearningPathStudentActivityGeneral
  ): void {
    this._subscriptions.push(
      this._learningPathService
        .updateManagerComment(
          studentActivity.studentActivityId,
          studentActivity.managerComment
        )
        .pipe(
          take(1),
          tap(() => {
            this._subscriptions.push(
              this.labelService
                .getLabel('savedsuccessfully', 'general')
                .subscribe((lbl) => {
                  this._toastrService.success(lbl.labelText);
                })
            );
          })
        )
        .subscribe()
    );
  }

  public getHistory(
    studentActivityId: number
  ): Observable<LearningPathStudentActivityStatusLog[]> {
    return this._learningPathService.getStudentActivityHistory(
      studentActivityId
    );
  }

  public sendReminder(learningPathStudentId: number) {
    this._subscriptions.push(
      this._learningPathService
        .sendReminder(learningPathStudentId)
        .pipe(
          take(1),
          tap(() => {
            this._subscriptions.push(
              this.labelService
                .getLabel('sentsuccessfully', 'general')
                .subscribe((lbl) => {
                  this._toastrService.success(lbl.labelText);
                })
            );
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    for (const subscription of this._subscriptions) {
      subscription.unsubscribe();
    }
  }
}
